import { Component, Input, OnInit, ViewEncapsulation } from '@angular/core';
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { EstudioCientificoService } from 'src/app/core/services/estudio-cientifico/estudio-cientifico.service';
import { MODAL_DIALOG_TYPES } from 'src/app/core/utils/modal-dialog-types';
import { DashboardProfesorModalComponent } from '../dashboard-estudio/dashboard-profesor-modal.component';
import { DetalleGrupoEstudioComponent } from './detalle-grupo-estudio/detalle-grupo-estudio.component';

export type GruposSortColumn = 'title' | 'nivel' | 'letras' | 'assignedStudents' | 'assignedProjects';
export type ProfesoresSortColumn = 'name' | 'surname' | 'email';
export type MandosSortColumn = 'idMando' | 'mac' | 'estado' | 'battery';
type SortDir = 'asc' | 'desc';

@Component({
	selector: 'app-detalle-centro-estudio',
	templateUrl: './detalle-centro-estudio.component.html',
	styleUrls: ['./detalle-centro-estudio.component.scss'],
	encapsulation: ViewEncapsulation.None,
	standalone: false
})
export class DetalleCentroEstudioComponent implements OnInit {

	@Input() idCenter: number;
	@Input() centerName: string = '';

	activeTab: 'grupos' | 'dashboard' | 'profesores' | 'mandos' = 'grupos';

	grupos: any[] = [];
	profesores: any[] = [];
	mandos: any[] = [];

	loadingGrupos: boolean = false;
	loadingProfesores: boolean = false;
	loadingMandos: boolean = false;

	searchGrupos: string = '';
	searchProfesores: string = '';
	searchMandos: string = '';

	gruposSortColumn: GruposSortColumn | null = null;
	gruposSortDir: SortDir = 'asc';

	profesoresSortColumn: ProfesoresSortColumn | null = null;
	profesoresSortDir: SortDir = 'asc';

	mandosSortColumn: MandosSortColumn | null = null;
	mandosSortDir: SortDir = 'asc';

	constructor(
		public activeModal: NgbActiveModal,
		private estudioService: EstudioCientificoService,
		private modalService: NgbModal
	) {}

	ngOnInit() {
		this.loadGrupos();
		this.loadProfesores();
		this.loadMandos();
	}

	closeModal() {
		this.activeModal.close();
	}

	setTab(tab: 'grupos' | 'dashboard' | 'profesores' | 'mandos') {
		this.activeTab = tab;
		if (tab === 'grupos' && this.grupos.length === 0) {
			this.loadGrupos();
		} else if (tab === 'profesores' && this.profesores.length === 0) {
			this.loadProfesores();
		} else if (tab === 'mandos' && this.mandos.length === 0) {
			this.loadMandos();
		}
	}

	loadGrupos() {
		this.loadingGrupos = true;
		this.estudioService.getGruposByCenter(this.idCenter).subscribe({
			next: (res) => {
				this.grupos = res?.data ?? (Array.isArray(res) ? res : []);
				this.loadingGrupos = false;
			},
			error: () => {
				this.loadingGrupos = false;
			}
		});
	}

	loadProfesores() {
		this.loadingProfesores = true;
		this.estudioService.getProfesoresByCenter(this.idCenter).subscribe({
			next: (res) => {
				this.profesores = res?.data ?? (Array.isArray(res) ? res : []);
				this.loadingProfesores = false;
			},
			error: () => {
				this.loadingProfesores = false;
			}
		});
	}

	loadMandos() {
		this.loadingMandos = true;
		this.estudioService.getMandosByCenter(this.idCenter).subscribe({
			next: (res) => {
				this.mandos = res?.data ?? (Array.isArray(res) ? res : []);
				this.loadingMandos = false;
			},
			error: () => {
				this.loadingMandos = false;
			}
		});
	}

	getEstadoLabel(estado: number): string {
		const labels: { [key: number]: string } = {
			0: 'Libre', 1: 'Asignado', 2: 'Perdido', 3: 'Estropeado'
		};
		return labels[estado] ?? 'Desconocido';
	}

	getEstadoClass(estado: number): string {
		const classes: { [key: number]: string } = {
			0: 'estado-libre', 1: 'estado-asignado', 2: 'estado-perdido', 3: 'estado-estropeado'
		};
		return classes[estado] ?? '';
	}

	verDetalleGrupo(group: any) {
		const modalRef = this.modalService.open(DetalleGrupoEstudioComponent, {
			scrollable: true,
			windowClass: `${MODAL_DIALOG_TYPES.W90} h-100`,
		});
		modalRef.componentInstance.group = group;
	}

	get gruposFiltrados(): any[] {
		const term = (this.searchGrupos || '').toLowerCase().trim();
		const filtered = !term ? [...this.grupos] : this.grupos.filter(g =>
			(g?.title ?? '').toString().toLowerCase().includes(term) ||
			(g?.nivel ?? '').toString().toLowerCase().includes(term) ||
			(g?.letras ?? '').toString().toLowerCase().includes(term) ||
			(g?.assignedStudents ?? '').toString().toLowerCase().includes(term) ||
			(g?.assignedProjects ?? '').toString().toLowerCase().includes(term)
		);
		const col = this.gruposSortColumn;
		if (!col) return filtered;
		const dir = this.gruposSortDir === 'asc' ? 1 : -1;
		const numericCols: GruposSortColumn[] = ['assignedStudents', 'assignedProjects'];
		return filtered.sort((a, b) => {
			if (numericCols.includes(col)) {
				return (((+a?.[col]) || 0) - ((+b?.[col]) || 0)) * dir;
			}
			const sa = (a?.[col] ?? '').toString().toLowerCase();
			const sb = (b?.[col] ?? '').toString().toLowerCase();
			if (sa < sb) return -1 * dir;
			if (sa > sb) return 1 * dir;
			return 0;
		});
	}

	get profesoresFiltrados(): any[] {
		const term = (this.searchProfesores || '').toLowerCase().trim();
		const filtered = !term ? [...this.profesores] : this.profesores.filter(p =>
			(p?.name ?? p?.firstName ?? '').toString().toLowerCase().includes(term) ||
			(p?.surname ?? p?.surName ?? '').toString().toLowerCase().includes(term) ||
			(p?.email ?? p?.mail ?? '').toString().toLowerCase().includes(term)
		);
		const col = this.profesoresSortColumn;
		if (!col) return filtered;
		const dir = this.profesoresSortDir === 'asc' ? 1 : -1;
		return filtered.sort((a, b) => {
			const sa = this.getProfesorField(a, col).toLowerCase();
			const sb = this.getProfesorField(b, col).toLowerCase();
			if (sa < sb) return -1 * dir;
			if (sa > sb) return 1 * dir;
			return 0;
		});
	}

	get mandosFiltrados(): any[] {
		const term = (this.searchMandos || '').toLowerCase().trim();
		const filtered = !term ? [...this.mandos] : this.mandos.filter(m =>
			(m?.idMando ?? '').toString().toLowerCase().includes(term) ||
			(m?.mac ?? '').toString().toLowerCase().includes(term) ||
			this.getEstadoLabel(m?.estado).toLowerCase().includes(term) ||
			(m?.battery ?? '').toString().toLowerCase().includes(term)
		);
		const col = this.mandosSortColumn;
		if (!col) return filtered;
		const dir = this.mandosSortDir === 'asc' ? 1 : -1;
		const numericCols: MandosSortColumn[] = ['idMando', 'battery'];
		return filtered.sort((a, b) => {
			if (col === 'estado') {
				const sa = this.getEstadoLabel(a?.estado).toLowerCase();
				const sb = this.getEstadoLabel(b?.estado).toLowerCase();
				if (sa < sb) return -1 * dir;
				if (sa > sb) return 1 * dir;
				return 0;
			}
			if (numericCols.includes(col)) {
				return (((+a?.[col]) || 0) - ((+b?.[col]) || 0)) * dir;
			}
			const sa = (a?.[col] ?? '').toString().toLowerCase();
			const sb = (b?.[col] ?? '').toString().toLowerCase();
			if (sa < sb) return -1 * dir;
			if (sa > sb) return 1 * dir;
			return 0;
		});
	}

	private getProfesorField(p: any, col: ProfesoresSortColumn): string {
		if (col === 'name') return (p?.name ?? p?.firstName ?? '').toString();
		if (col === 'surname') return (p?.surname ?? p?.surName ?? '').toString();
		if (col === 'email') return (p?.email ?? p?.mail ?? '').toString();
		return '';
	}

	sortGrupos(col: GruposSortColumn) {
		if (this.gruposSortColumn === col) {
			this.gruposSortDir = this.gruposSortDir === 'asc' ? 'desc' : 'asc';
		} else {
			this.gruposSortColumn = col;
			const numericCols: GruposSortColumn[] = ['assignedStudents', 'assignedProjects'];
			this.gruposSortDir = numericCols.includes(col) ? 'desc' : 'asc';
		}
	}

	getGruposSortIcon(col: GruposSortColumn): string {
		if (this.gruposSortColumn !== col) return '↕';
		return this.gruposSortDir === 'asc' ? '↑' : '↓';
	}

	sortProfesores(col: ProfesoresSortColumn) {
		if (this.profesoresSortColumn === col) {
			this.profesoresSortDir = this.profesoresSortDir === 'asc' ? 'desc' : 'asc';
		} else {
			this.profesoresSortColumn = col;
			this.profesoresSortDir = 'asc';
		}
	}

	getProfesoresSortIcon(col: ProfesoresSortColumn): string {
		if (this.profesoresSortColumn !== col) return '↕';
		return this.profesoresSortDir === 'asc' ? '↑' : '↓';
	}

	sortMandos(col: MandosSortColumn) {
		if (this.mandosSortColumn === col) {
			this.mandosSortDir = this.mandosSortDir === 'asc' ? 'desc' : 'asc';
		} else {
			this.mandosSortColumn = col;
			const numericCols: MandosSortColumn[] = ['idMando', 'battery'];
			this.mandosSortDir = numericCols.includes(col) ? 'desc' : 'asc';
		}
	}

	getMandosSortIcon(col: MandosSortColumn): string {
		if (this.mandosSortColumn !== col) return '↕';
		return this.mandosSortDir === 'asc' ? '↑' : '↓';
	}

	verDashboardProfesor(profesor: any) {
		const idUser = profesor?.idUser ?? profesor?.id_user;
		if (!idUser || !this.idCenter) return;
		const nombre = `${profesor.name || profesor.firstName || ''} ${profesor.surname || profesor.surName || ''}`.trim() || 'Profesor';
		const modalRef = this.modalService.open(DashboardProfesorModalComponent, {
			scrollable: true,
			windowClass: `${MODAL_DIALOG_TYPES.W90} h-100`,
		});
		modalRef.componentInstance.idCenter = this.idCenter;
		modalRef.componentInstance.idUser = idUser;
		modalRef.componentInstance.title = nombre;
		modalRef.componentInstance.subtitle = this.centerName;
	}
}
